import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Banknote, CalendarDays, CircleDollarSign, DoorOpen, GraduationCap, Link2, Music } from "lucide-react";
import { Link } from "react-router-dom";
import { api, fmtBR, fmtBRL, fmtDate, mondayOf, addDays, WEEKDAY_NAMES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ courses: [], teachers: [], rooms: [], lessons: [] });
  const [fin, setFin] = useState(null);
  const [ped, setPed] = useState(null);
  const [dueContacts, setDueContacts] = useState([]);
  const [myTeacher, setMyTeacher] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const canViewFin = ["admin", "direcao", "secretaria"].includes(user.role);
  const canViewPed = ["admin", "direcao"].includes(user.role);
  const isProfessor = user.role === "professor";

  useEffect(() => {
    const load = async () => {
      const monday = mondayOf(new Date());
      const sunday = addDays(monday, 6);
      const [courses, teachers, rooms, lessons] = await Promise.all([
        api.get("/courses"),
        api.get("/teachers").catch(() => ({ data: [] })),
        api.get("/rooms"),
        api.get("/lessons", { params: { start: fmtDate(monday), end: fmtDate(sunday) } }),
      ]);
      setData({
        courses: courses.data,
        teachers: teachers.data,
        rooms: rooms.data,
        lessons: lessons.data,
      });
      if (isProfessor) {
        try {
          const { data: me } = await api.get("/teachers/me");
          setMyTeacher(me);
        } catch {
          setMyTeacher(null);
        }
      }
      if (canViewFin) {
        try {
          const { data: charges } = await api.get("/financial/charges", {
            params: { reference_month: fmtDate(new Date()).slice(0, 7) },
          });
          const t = { aberto: 0, recebido: 0, vencidasValor: 0, vencidasCount: 0 };
          charges.forEach((c) => {
            if (c.status === "cancelada") return;
            t.recebido += c.paid_amount || 0;
            const openAmount = c.amount - (c.paid_amount || 0);
            if (["pendente", "parcial", "vencida"].includes(c.status)) t.aberto += openAmount;
            if (c.status === "vencida") {
              t.vencidasValor += openAmount;
              t.vencidasCount += 1;
            }
          });
          setFin(t);
        } catch {
          setFin(null);
        }
        try {
          const { data: leads } = await api.get("/leads");
          const limit = new Date(Date.now() + 48 * 3600 * 1000);
          setDueContacts(
            leads
              .filter((l) => l.next_contact_at && !["convertido", "perdido", "desistiu"].includes(l.status) && new Date(l.next_contact_at) <= limit)
              .sort((a, b) => new Date(a.next_contact_at) - new Date(b.next_contact_at))
          );
        } catch {
          setDueContacts([]);
        }
      }
      if (canViewPed) {
        try {
          const { data: p } = await api.get("/pedagogy/indicators");
          setPed(p);
        } catch {
          setPed(null);
        }
      }
      setLoading(false);
    };
    load().catch(() => setLoading(false));
  }, []);

  const today = fmtDate(new Date());
  const todayLessons = useMemo(
    () => data.lessons.filter((l) => l.date === today && l.status !== "cancelada"),
    [data.lessons, today]
  );
  const teacherName = (id) => data.teachers.find((t) => t.id === id)?.name || "—";
  const courseName = (id) => data.courses.find((c) => c.id === id)?.name || "—";
  const roomName = (id) => data.rooms.find((r) => r.id === id)?.name || "—";

  const stats = isProfessor
    ? [
        { label: "Minhas aulas esta semana", value: data.lessons.filter((l) => l.status !== "cancelada").length, icon: CalendarDays, testId: "stat-my-week-lessons" },
        { label: "Minhas aulas hoje", value: todayLessons.length, icon: CalendarDays, testId: "stat-my-today-lessons" },
        { label: "Meus cursos", value: myTeacher?.course_ids?.length ?? 0, icon: Music, testId: "stat-my-courses", detail: (myTeacher?.course_names || []).join(" · ") },
      ]
    : [
        { label: "Cursos ativos", value: data.courses.filter((c) => c.active).length, icon: Music, testId: "stat-courses" },
        { label: "Professores ativos", value: data.teachers.filter((t) => t.active).length, icon: GraduationCap, testId: "stat-teachers" },
        { label: "Salas", value: data.rooms.filter((r) => r.active).length, icon: DoorOpen, testId: "stat-rooms" },
        { label: "Aulas esta semana", value: data.lessons.filter((l) => l.status !== "cancelada").length, icon: CalendarDays, testId: "stat-lessons" },
      ];

  if (loading) return <p className="text-sm text-muted-foreground">Carregando...</p>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Olá, {user.name.split(" ")[0]}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isProfessor ? "Sua visão de trabalho." : "Visão geral do Instituto Musical Jacaré."}
        </p>
      </div>

      {isProfessor && myTeacher === null ? (
        <div className="border border-amber-300 bg-amber-50 p-5" data-testid="dashboard-unlinked-teacher">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
            <Link2 size={18} className="text-amber-600" /> Conta não vinculada
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Sua conta ainda não está vinculada a um cadastro de professor. A secretaria precisa realizar esse vínculo
            para exibir suas aulas e seus cursos.
          </p>
        </div>
      ) : (
        <div className={`grid grid-cols-2 gap-4 ${isProfessor ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
          {stats.map((s) => (
            <div key={s.label} className="border border-border bg-white p-5" data-testid={s.testId}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{s.label}</p>
                <s.icon size={18} className="text-primary" />
              </div>
              <p className="mt-3 font-heading text-3xl font-bold">{s.value}</p>
              {s.detail && <p className="mt-1 text-xs text-muted-foreground">{s.detail}</p>}
            </div>
          ))}
        </div>
      )}

      {dueContacts.length > 0 && (
        <div className="border border-destructive/50 bg-destructive/5 p-5" data-testid="dashboard-contacts-alert">
          <div className="flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-lg font-semibold">
              <AlertTriangle size={18} className="text-destructive" /> Contatos a realizar — {dueContacts.length}
            </h2>
            <Link to="/admin/interessados" className="text-sm font-medium text-primary transition-colors hover:underline" data-testid="dashboard-contacts-link">
              Ver interessados
            </Link>
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {dueContacts.slice(0, 5).map((l) => (
              <li key={l.id} data-testid={`dashboard-contact-${l.id}`}>
                <span className="font-medium">{l.full_name}</span>
                <span className="text-muted-foreground"> · {l.course_name || ""} · próximo contato: {new Date(l.next_contact_at).toLocaleString("pt-BR")}</span>
                {new Date(l.next_contact_at) < new Date() && <span className="ml-1 font-semibold text-destructive">(vencido)</span>}
              </li>
            ))}
            {dueContacts.length > 5 && <li className="text-xs text-muted-foreground">+ {dueContacts.length - 5} outro(s)</li>}
          </ul>
        </div>
      )}

      {fin && (
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">
              Financeiro — {fmtDate(new Date()).slice(5, 7)}/{fmtDate(new Date()).slice(0, 4)}
            </h2>
            <Link to="/admin/financeiro" className="text-sm font-medium text-primary transition-colors hover:underline" data-testid="dashboard-financial-link">
              Ver financeiro
            </Link>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="dashboard-financial-cards">
            <div className="border border-border bg-white p-5" data-testid="stat-fin-open">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">A receber no mês</p>
                <CircleDollarSign size={18} className="text-primary" />
              </div>
              <p className="mt-3 font-heading text-3xl font-bold">{fmtBRL(fin.aberto)}</p>
            </div>
            <div className="border border-border bg-white p-5" data-testid="stat-fin-received">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Recebido no mês</p>
                <Banknote size={18} className="text-primary" />
              </div>
              <p className="mt-3 font-heading text-3xl font-bold">{fmtBRL(fin.recebido)}</p>
            </div>
            <div className="border border-border bg-white p-5" data-testid="stat-fin-overdue">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Cobranças vencidas</p>
                <AlertTriangle size={18} className="text-destructive" />
              </div>
              <p className="mt-3 font-heading text-3xl font-bold">{fmtBRL(fin.vencidasValor)}</p>
              <p className="mt-1 text-xs text-muted-foreground">{fin.vencidasCount} cobrança(s)</p>
            </div>
          </div>
        </div>
      )}

      {ped && (
        <div>
          <h2 className="font-heading text-lg font-semibold">Indicadores pedagógicos</h2>
          <div className="mt-3 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="dashboard-pedagogy-cards">
            <div className="border border-border bg-white p-4" data-testid="ind-active-students">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Alunos ativos</p>
              <p className="mt-2 font-heading text-2xl font-bold">{ped.alunos_ativos}</p>
            </div>
            <div className="border border-border bg-white p-4" data-testid="ind-active-enrollments">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Matrículas ativas</p>
              <p className="mt-2 font-heading text-2xl font-bold">{ped.matriculas_ativas}</p>
            </div>
            <div className="border border-border bg-white p-4" data-testid="ind-avg-frequency">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Frequência média</p>
              <p className="mt-2 font-heading text-2xl font-bold">{ped.frequencia_media != null ? `${ped.frequencia_media}%` : "—"}</p>
            </div>
            <div className="border border-border bg-white p-4" data-testid="ind-avg-progress">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Progresso médio</p>
              <p className="mt-2 font-heading text-2xl font-bold">{ped.progresso_medio != null ? `${ped.progresso_medio}%` : "—"}</p>
            </div>
            <div className="border border-border bg-white p-4" data-testid="ind-no-record">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Sem registro ({ped.days}d)</p>
              <p className="mt-2 font-heading text-2xl font-bold">{ped.sem_registro.count}</p>
            </div>
          </div>
          {ped.sem_registro.students.length > 0 && (
            <div className="mt-3 border border-border bg-white p-4" data-testid="dashboard-no-record-list">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Sem registro pedagógico no período {fmtBR(ped.period.from)} — {fmtBR(ped.period.to)} (referência: {fmtBR(ped.reference_date)})
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                {ped.sem_registro.students.slice(0, 5).map((s) => (
                  <li key={s.student_id} data-testid={`no-record-student-${s.student_id}`}>
                    {s.student_name}
                    <span className="text-muted-foreground"> · último registro: {s.last_record_date ? fmtBR(s.last_record_date) : "nunca"}</span>
                  </li>
                ))}
                {ped.sem_registro.students.length > 5 && (
                  <li className="text-xs text-muted-foreground">+ {ped.sem_registro.students.length - 5} outro(s)</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <div>
        <h2 className="font-heading text-lg font-semibold">
          {isProfessor ? "Minhas aulas de hoje" : "Aulas de hoje"} — {WEEKDAY_NAMES[(new Date().getDay() + 6) % 7]}
        </h2>
        <div className="mt-3 border border-border bg-white" data-testid="today-lessons">
          {todayLessons.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground" data-testid="today-lessons-empty">
              Nenhuma aula agendada para hoje.
            </p>
          ) : (
            todayLessons.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-border p-4 last:border-b-0" data-testid={`today-lesson-${l.id}`}>
                <span className="font-heading text-base font-bold">
                  {l.start_time}–{l.end_time}
                </span>
                <span className="text-sm font-medium">{teacherName(l.teacher_id)}</span>
                <span className="text-sm text-muted-foreground">{courseName(l.course_id)}</span>
                <span className="ml-auto text-xs uppercase tracking-wider text-muted-foreground">{roomName(l.room_id)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
